import { useEffect, useRef, useState } from 'react'

import * as d3 from 'd3'
import { LngLat } from 'mapbox-gl'
import { tileToBBOX } from '@mapbox/tilebelt'

import {
  useFilterStore,
  useMapStore,
  usePointStore,
  usePropertiesStore
} from '@monopolio-ui/state-library'
import { ICluster, EPropertyType } from '@monopolio-ui/types-library'
import { checkBoundsIntersects, getDesiredZoomLevel } from '../../utils'
import { usePrevious } from '@wiggot/hooks/src/usePrevious'

const themeColors = {
  [EPropertyType.SALE]: {
    enabled: '#222',
    hover: '#97CC04'
  },
  [EPropertyType.RENT]: {
    enabled: '#222',
    hover: '#00C0C4'
  }
}

/**
 * This component is used to manage the points in the map
 * @returns {JSX.Element} - The JSX.Element
 */
const ListingsClusterManager = (): JSX.Element => {
  const requiredTiles: string[] = []

  const { map, drawingTool, hoveredLayer, setHoveredLayer } = useMapStore()

  const {
    tileQueryParams,
    pointsSvgContainer,
    isEnablePropertiesPoints,
    loadedClusters
  } = usePointStore()

  const { propertyType } = useFilterStore()

  const { hoveredPropertyCard, setHoveredPropertyCard } = usePropertiesStore()

  const hoveredPropertyCardRef =
    useRef<typeof hoveredPropertyCard>(hoveredPropertyCard)
  hoveredPropertyCardRef.current = hoveredPropertyCard

  const isEnablePropertiesPointsRef = useRef<typeof isEnablePropertiesPoints>(
    isEnablePropertiesPoints
  )
  isEnablePropertiesPointsRef.current = isEnablePropertiesPoints

  const tileQueryParamsRef = useRef<typeof tileQueryParams>(tileQueryParams)
  tileQueryParamsRef.current = tileQueryParams

  const pointsSvgContainerRef =
    useRef<typeof pointsSvgContainer>(pointsSvgContainer)
  pointsSvgContainerRef.current = pointsSvgContainer

  const drawingToolRef = useRef<typeof drawingTool>(drawingTool)
  drawingToolRef.current = drawingTool

  const propertyTypeRef = useRef<EPropertyType>(
    propertyType ?? EPropertyType.SALE
  )
  propertyTypeRef.current = propertyType ?? EPropertyType.SALE

  const hoveredLayerRef = useRef<typeof hoveredLayer>(hoveredLayer)
  hoveredLayerRef.current = hoveredLayer

  const [clusterHovered, setClusterHovered] = useState<{
    id: number
    instance: any
  }>()

  const clusterHoveredPrevious =
    usePrevious<typeof clusterHovered>(clusterHovered)

  function project(dValue: number[]) {
    return map?.project(new LngLat(dValue[0], dValue[1]))
  }

  const createListingsClusters = (params: {
    key: string
    spatialIndices: {
      xIndex: number
      yIndex: number
      zIndex: number
    }
    features: ICluster[]
  }): void => {
    const {
      spatialIndices: { xIndex, yIndex, zIndex },
      features
    } = params

    const { minZoom, maxZoom } = getDesiredZoomLevel({
      zoom: zIndex
    })

    const spatialIndex = `${xIndex}-${yIndex}-${zIndex}`

    if (requiredTiles?.includes(spatialIndex) || !pointsSvgContainer) {
      return
    }

    requiredTiles?.push(spatialIndex)

    let group: any

    const createGroup = (): void => {
      group = pointsSvgContainer
        ?.append('g')
        .attr('id', `group-${spatialIndex}-clusters`)

      const points = group
        ?.selectAll('g.cluster')
        ?.data(features)
        ?.enter()
        ?.append('g')
        ?.attr('class', `cluster cluster-${spatialIndex}`)
        ?.on('mouseover', (dEvent: any, dData: ICluster) => {
          if (
            drawingToolRef?.current?.isActive &&
            drawingToolRef?.current?.status !== 'processed'
          ) {
            return
          }

          pointsSvgContainerRef?.current?.style('cursor', 'pointer')

          setHoveredPropertyCard(undefined)
          setHoveredLayer({
            layerId: 'cluster',
            properties: undefined
          })

          const point = d3.select(dEvent?.currentTarget)

          setClusterHovered({
            id: dData?.properties?.clusterId,
            instance: point
          })
        })
        .on('mouseout', () => {
          setClusterHovered(undefined)
          setHoveredLayer(undefined)

          if (
            drawingToolRef?.current?.isActive &&
            drawingToolRef?.current?.status !== 'processed'
          ) {
            return
          }

          pointsSvgContainerRef?.current?.style('cursor', 'grab')
        })
        .on('click', (_: any, dData: ICluster) => {
          if (
            drawingToolRef?.current?.isActive &&
            drawingToolRef?.current?.status !== 'processed'
          ) {
            return
          }

          const { geometry } = dData
          const coordinates = geometry?.coordinates
          const [lng, lat] = coordinates

          const { maxZoom } = getDesiredZoomLevel({
            zoom: (map?.getZoom() ?? 0) + 0.1
          })

          map?.flyTo({
            center: [lng ?? 0, lat ?? 0],
            zoom: maxZoom,
            duration: 500
          })
        })

      points
        ?.append('circle')
        ?.attr('r', (dValue: { properties: { pointCount: number } }) => {
          if (dValue?.properties?.pointCount < 10) {
            return 22
          }
          if (dValue?.properties?.pointCount < 100) {
            return 24
          }

          return 26
        })
        .style('fill', () => {
          return themeColors[propertyTypeRef?.current]?.enabled
        })
        .style('stroke', '#D1D1D1')
        .style('stroke-width', 1)

      points
        ?.append('text')
        ?.attr('x', 0)
        ?.attr('y', () => {
          return -8
        })
        ?.attr('text-anchor', 'middle')
        ?.attr('dy', '12px')
        ?.attr('fill', '#FFFFFF')
        ?.attr('font-family', 'Ret Hat Display')
        ?.attr('font-weight', '700')
        ?.text((dValue: { properties: { pointCount: any } }) => {
          return dValue?.properties?.pointCount
        })

      render()
    }

    createGroup()

    function render() {
      if (
        params.key !== tileQueryParamsRef?.current ||
        !isEnablePropertiesPointsRef?.current
      ) {
        group?.remove()
        group = null

        if (hoveredLayerRef?.current?.layerId === 'cluster') {
          setHoveredLayer(undefined)
          pointsSvgContainerRef?.current?.style('cursor', 'grab')
        }

        map?.off('move', render)
        map?.off('moveend', render)
        return
      }

      const mapBounds: any = map?.getBounds()
      const tileBounds: any = tileToBBOX([xIndex, yIndex, zIndex])

      const intersects = checkBoundsIntersects({
        mapBounds,
        tileBounds
      })

      const currentZoom = map?.getZoom() || 0
      const isZoomed = currentZoom >= minZoom && currentZoom <= maxZoom

      if (intersects && isZoomed) {
        if (!group) {
          createGroup()
        }
        group
          .selectAll('g.cluster')
          .attr(
            'transform',
            (dValue: { geometry: { coordinates: number[] } }) => {
              const coordinates = project(dValue?.geometry?.coordinates)
              return `translate(${coordinates?.x},${coordinates?.y})`
            }
          )

        if (hoveredPropertyCardRef?.current?.id) {
          const { latitude, longitude } = hoveredPropertyCardRef?.current || {}

          const isInside =
            longitude >= tileBounds[0] &&
            longitude <= tileBounds[2] &&
            latitude >= tileBounds[1] &&
            latitude <= tileBounds[3]

          if (isInside) {
            hoverClusterByPropertyCard({
              classKey: `cluster-${spatialIndex}`
            })
          }
        }
      } else if (group) {
        group.remove()
        group = null

        if (hoveredLayerRef?.current?.layerId === 'cluster') {
          setHoveredLayer(undefined)
          pointsSvgContainerRef?.current?.style('cursor', 'grab')
        }
      }
    }

    map?.on('move', render)
    map?.on('moveend', render)
  }

  const illuminateCluster = (params: { clusterInstance: any }): void => {
    const { clusterInstance } = params

    if (!clusterInstance?.size()) {
      return
    }

    clusterInstance?.raise()

    const circle = clusterInstance?.select('circle')
    circle
      ?.style('fill', () => {
        return themeColors[propertyTypeRef?.current]?.hover
      })
      .style('stroke', '#FFFFFF')
      .transition()
      .duration(200)
      .style('stroke-width', 3)
      .attr('r', (dValue: { properties: { pointCount: number } }) => {
        if (dValue?.properties?.pointCount < 10) {
          return 23
        }
        if (dValue?.properties?.pointCount < 100) {
          return 25
        }

        return 27
      })
  }

  const turnOffClusters = (): void => {
    const instance = pointsSvgContainerRef?.current?.selectAll('g.cluster')

    if (!instance?.size()) {
      return
    }

    const circle = instance?.select('circle')

    circle
      ?.style('fill', () => {
        return themeColors[propertyTypeRef?.current]?.enabled
      })
      ?.style('stroke', '#D1D1D1')
      ?.transition()
      ?.duration(200)
      ?.style('stroke-width', 1.5)
      ?.attr('r', (dValue: { properties: { pointCount: number } }) => {
        if (dValue?.properties?.pointCount < 10) {
          return 22
        }
        if (dValue.properties?.pointCount < 100) {
          return 24
        }

        return 26
      })
  }

  const hoverClusterByPropertyCard = (params?: { classKey?: string }) => {
    const { classKey } = params || {}

    const getAllClusters = (): {
      id: number
      instance: any
      coordinates: number[]
    }[] => {
      const allPoints = pointsSvgContainerRef?.current?.selectAll(
        `g.${classKey}` || 'g.cluster'
      )

      const clusters = allPoints?.nodes()?.map((node: any) => {
        const data: any = d3?.select(node)?.datum()
        return {
          instance: d3?.select(node),
          coordinates: data?.geometry?.coordinates,
          id: data?.properties?.clusterId
        }
      })

      return clusters
    }

    const findPoint = (id: string) => {
      const allPoints =
        pointsSvgContainerRef?.current?.selectAll('g.listingPoint')

      const find = allPoints?.filter((dValue: any) => {
        const { properties } = dValue
        return properties?.id === id
      })

      if (!find?.size()) {
        return undefined
      }

      return find
    }

    if (!hoveredPropertyCardRef?.current?.id || !google?.maps) {
      return
    }

    const allClusters = getAllClusters()

    if (!allClusters?.length) {
      return
    }

    const { latitude, longitude } = hoveredPropertyCardRef?.current || {}

    let minDistance = Infinity
    let closestCluster:
      | {
          id: number
          instance: any
        }
      | undefined

    if (!window.google || !window.google.maps) {
      return
    }

    const myLatLng = new google.maps.LatLng(latitude, longitude)

    allClusters?.forEach(({ coordinates, id, instance }) => {
      const circleCenter = new google.maps.LatLng(
        coordinates[1] ?? 0,
        coordinates[0] ?? 0
      )
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        circleCenter,
        myLatLng
      )

      if (distance < minDistance) {
        minDistance = distance
        closestCluster = {
          id,
          instance
        }
      }
    })

    if (!closestCluster) {
      return
    }

    const point = findPoint(hoveredPropertyCardRef?.current?.id)
    if (point) {
      setClusterHovered(undefined)
      return
    }

    setClusterHovered({
      id: closestCluster?.id,
      instance: closestCluster?.instance
    })
  }

  useEffect(() => {
    if (loadedClusters?.length && pointsSvgContainer) {
      const lastGroup = loadedClusters[loadedClusters?.length - 1]
      if (!lastGroup?.features?.length) {
        return
      }
      createListingsClusters(lastGroup)
    }
  }, [loadedClusters, pointsSvgContainer])

  useEffect(() => {
    if (hoveredPropertyCard?.id) {
      hoverClusterByPropertyCard()
    }
  }, [hoveredPropertyCard])

  useEffect(() => {
    if (clusterHoveredPrevious?.id) {
      turnOffClusters()
    }

    if (clusterHovered?.id) {
      illuminateCluster({
        clusterInstance: clusterHovered?.instance
      })
    }
  }, [clusterHovered])

  useEffect(() => {
    if (hoveredLayer?.layerId !== 'cluster') {
      turnOffClusters()
    }
  }, [hoveredLayer])

  return <></>
}

export default ListingsClusterManager
